/**
 * Single cart line item — image, name/brand, qty stepper, savings, deals.
 * Pure presentation; parent passes the line + helpers.
 */
import { Plus, Minus, Trash2 } from "lucide-react";
import { formatMoney } from "../../lib/cart";
import CartLineDealBadges from "../../components/CartLineDealBadges";

export default function CartLineItem({
    item,
    loading,
    specials,
    progresses,
    storeSlug,
    onIncrement,
    onDecrement,
    onRemove,
}) {
    const img = item.product?.images?.[0]?.url;
    const qty = Math.max(1, item.quantity);
    const variant =
        (item.product?.variants || []).find((v) => v.option === item.option) ||
        (item.product?.variants || [])[0];
    // Dutchie returns prices in dollars; multiply by 100 to express in cents
    // for the formatMoney helper.
    const regularUnit = variant?.priceRec || 0;
    const specialUnit = variant?.specialPriceRec;
    const onSale = specialUnit && regularUnit && specialUnit < regularUnit;
    const lineRegularCents = regularUnit * qty * 100;
    const lineCurrentCents = onSale
        ? specialUnit * qty * 100
        : item.basePrice || lineRegularCents;
    const savingsCents = Math.max(0, lineRegularCents - lineCurrentCents);
    const showSavings = savingsCents > 50; // > $0.50

    return (
        <li data-testid={`cart-item-${item.id}`} className="flex gap-3">
            <div className="w-20 h-20 bg-zen-surface border border-zen-ink/10 overflow-hidden shrink-0">
                {img && (
                    <img
                        src={img}
                        alt={item.product?.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                    />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3 mb-1">
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.22em] text-zen-olive mb-1 truncate">
                            {item.product?.brand?.name || item.product?.category}
                        </p>
                        <p className="font-serif text-base text-zen-ink leading-tight line-clamp-2">
                            {item.product?.name}
                        </p>
                    </div>
                    <div className="text-right shrink-0">
                        {showSavings && (
                            <span
                                data-testid={`cart-line-savings-${item.id}`}
                                className="inline-block bg-red-100 text-red-700 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 mb-1 leading-none"
                            >
                                {formatMoney(savingsCents)} off
                            </span>
                        )}
                        <p
                            data-testid={`cart-line-total-${item.id}`}
                            className="font-serif text-lg text-zen-ink font-light leading-none"
                        >
                            {formatMoney(lineCurrentCents)}
                        </p>
                        {showSavings && (
                            <p
                                data-testid={`cart-line-orig-${item.id}`}
                                className="text-xs text-zen-ink-soft line-through leading-none mt-1"
                            >
                                {formatMoney(lineRegularCents)}
                            </p>
                        )}
                    </div>
                </div>
                <p className="text-[11px] text-zen-ink-soft mb-2">
                    {item.option}
                    {regularUnit
                        ? ` · ${formatMoney(
                              (onSale ? specialUnit : regularUnit) * 100
                          )} ea`
                        : ""}
                </p>
                <CartLineDealBadges
                    item={item}
                    specials={specials}
                    progresses={progresses}
                    storeSlug={storeSlug}
                />
                <div className="flex items-center gap-3">
                    <div className="inline-flex items-center border border-zen-ink/15">
                        <button
                            onClick={onDecrement}
                            disabled={loading}
                            data-testid={`cart-qty-dec-${item.id}`}
                            className="px-2.5 py-1.5 text-zen-ink hover:bg-zen-ink/5 disabled:opacity-40"
                            aria-label="Decrease quantity"
                        >
                            <Minus size={12} />
                        </button>
                        <span className="px-3 text-sm font-mono text-zen-ink">
                            {item.quantity}
                        </span>
                        <button
                            onClick={onIncrement}
                            disabled={loading}
                            data-testid={`cart-qty-inc-${item.id}`}
                            className="px-2.5 py-1.5 text-zen-ink hover:bg-zen-ink/5 disabled:opacity-40"
                            aria-label="Increase quantity"
                        >
                            <Plus size={12} />
                        </button>
                    </div>
                    <button
                        onClick={onRemove}
                        disabled={loading}
                        data-testid={`cart-remove-${item.id}`}
                        aria-label="Remove item"
                        className="text-zen-ink-soft hover:text-zen-olive disabled:opacity-40"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>
        </li>
    );
}
